import { getConsentConfig, type ConsentConfig } from '@kraftwerkdesign/consent'

export const consentConfig: Partial<ConsentConfig> = {
  cookieName: 'wallace_cookie_consent',
  privacyPolicyUrl: '/privacy-policy',
  mode: 'opt-out',
  categories: [
    {
      id: 'necessary',
      enabled: true,
      readOnly: true,
      google: ['security_storage', 'functionality_storage'],
      copy: {
        title: 'Strictly necessary',
        description:
          'Required for core functionality, accessibility, security and performance of the website. These cannot be disabled.',
      },
    },
    {
      id: 'analytics',
      analytics: true,
      enabled: true,
      meta: true,
      google: [
        'analytics_storage',
        'ad_storage',
        'ad_user_data',
        'ad_personalization',
      ],
      autoClear: [
        { name: /^_ga/ },
        { name: '_gid' },
        { name: /^_gcl/ },
        { name: /^_fbp/ },
        { name: /^dd_/ },
      ],
      copy: {
        title: 'Analytics & marketing',
        description:
          'Includes analytics and ad tracking, social media plugins and email newsletter tools. These services may set cookies to measure engagement, personalize communications, or support marketing — even when their main purpose is chat or signup.',
      },
    },
  ],
  buildCopy: (gpcActive) => {
    const { privacyPolicyUrl, categories, allowGpcOverride } = getConsentConfig()
    const gpcLocked = gpcActive && !allowGpcOverride

    const gpcConsentDescription = gpcLocked
      ? "Your browser's Global Privacy Control (GPC) signal is on. We've applied your choice — only strictly necessary cookies are in use and analytics or marketing tools will not load. You can review details anytime."
      : "Your browser's Global Privacy Control (GPC) signal is on, so only strictly necessary cookies are in use by default. You can turn on analytics and marketing anytime under Manage preferences."

    const gpcChoicesDescription = gpcLocked
      ? 'Global Privacy Control (GPC) is enabled in your browser. Analytics and marketing cookies are turned off and cannot be enabled while this signal is active.'
      : 'Global Privacy Control (GPC) is enabled in your browser, so analytics and marketing cookies are turned off by default. You can choose to enable them below — doing so overrides the GPC signal for this site.'

    const categorySections = categories.map((category) => ({
      title: category.copy?.title ?? category.id,
      description: category.copy?.description ?? '',
      linkedCategory: category.id,
    }))

    return {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: gpcActive ? 'Your privacy settings' : 'Your Privacy',
            description: gpcActive
              ? gpcConsentDescription
              : 'We use cookies to keep our site running and (with your consent) to understand how you found us. You can change your mind anytime.',
            acceptAllBtn: gpcActive ? undefined : 'Accept all',
            acceptNecessaryBtn: gpcActive ? 'Got it' : undefined,
            showPreferencesBtn: 'Manage preferences',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: gpcLocked ? undefined : 'Accept all',
            acceptNecessaryBtn: 'Reject non-essential',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'Your privacy choices',
                description: gpcActive
                  ? gpcChoicesDescription
                  : 'You can change these settings at any time. Analytics and marketing cookies are on by default; you can turn them off below.',
              },
              ...categorySections,
              {
                title: 'More information',
                description: `For more information about our policy on cookies and your choices, please view our <a class="cc-link" href="${privacyPolicyUrl}">privacy policy</a>.`,
              },
            ],
          },
        },
      },
    }
  },
}
